"use client";
import {
	createContext,
	useContext,
	useState,
	useCallback,
	useRef,
} from "react";

type TransitionState = "idle" | "exiting" | "waiting" | "entering";

type TransitionContextType = {
	transitionState: TransitionState;
	startExitTransition: (callback: () => void) => void;
	completeNavigation: () => void;
	startEnterTransition: () => void;
	finishTransition: () => void;
};

const TransitionContext = createContext<TransitionContextType | undefined>(
	undefined
);

export const useTransition = () => {
	const context = useContext(TransitionContext);
	if (!context) {
		throw new Error("useTransition must be used within TransitionProvider");
	}
	return context;
};

export const TransitionProvider = ({
	children,
}: {
	children: React.ReactNode;
}) => {
	const [transitionState, setTransitionState] =
		useState<TransitionState>("idle");
	const navigationCallbackRef = useRef<(() => void) | null>(null);

	const startExitTransition = useCallback((callback: () => void) => {
		navigationCallbackRef.current = callback;
		setTransitionState("exiting");
	}, []);

	const completeNavigation = useCallback(() => {
		// Execute the navigation callback and wait for page to load
		if (navigationCallbackRef.current) {
			navigationCallbackRef.current();
			navigationCallbackRef.current = null;
		}
		setTransitionState("waiting");
	}, []);

	const startEnterTransition = useCallback(() => {
		setTransitionState("entering");
	}, []);

	const finishTransition = useCallback(() => {
		setTransitionState("idle");
	}, []);

	return (
		<TransitionContext.Provider
			value={{
				transitionState,
				startExitTransition,
				completeNavigation,
				startEnterTransition,
				finishTransition,
			}}
		>
			{children}
		</TransitionContext.Provider>
	);
};
